import { ReactNode } from 'react';
import Image from 'next/image';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { SxProps, Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import BrandDefinition from '@/types/BrandDefinition';
import { LOGO_SOURCE } from '@/constants/brand-images';
import { ROUTES } from '@/constants/application-urls';
import LocalizedLink from '@/components/LocalizedLink';

interface HeaderDrawerProps {
	open: boolean;
	onClose?: () => void;
	brand: BrandDefinition;
	children: ReactNode;
	iconButtonSx?: SxProps<Theme>;
	anchor?: 'left' | 'right';
}

export default function HeaderDrawer({
	open,
	onClose,
	brand,
	children,
	iconButtonSx,
	anchor = 'left',
}: HeaderDrawerProps) {
	return (
		<Drawer
			anchor={anchor}
			open={open}
			onClose={onClose}
			sx={{
				'& .MuiDrawer-paper': {
					width: '100%',
					height: '100%',
					backgroundColor: 'white.main',
					padding: '20px 0',
					overflow: 'hidden',
				},
			}}
			data-testid="header-drawer"
		>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<LocalizedLink
					href={ROUTES.HOME}
					sx={{
						height: {
							xs: '27px',
							md: '41px',
						},
						mr: {
							xs: '0',
							md: 'auto',
						},
						position: 'absolute',
						left: '50%',
						transform: 'translateX(-50%)',
					}}
					data-testid="header-navigation-logo"
				>
					<Image
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'contain',
						}}
						width={200}
						height={40}
						src={LOGO_SOURCE[brand.brandCode]}
						alt={`${brand.brandName} logo`}
						priority
					/>
				</LocalizedLink>
				<IconButton
					color="inherit"
					aria-label="close"
					data-testid="header-mobile-close"
					onClick={onClose}
					sx={iconButtonSx || { ml: 'auto', mr: '8px' }}
				>
					<CloseIcon />
				</IconButton>
			</Box>
			{children}
		</Drawer>
	);
}
